// =============================================================================
// iTravel 2.0 — Trip Hooks (TanStack Query) — Offline-Aware
// =============================================================================
// Custom hooks that wrap trip API calls with TanStack Query mutations/queries.
// These hooks handle caching, invalidation, optimistic updates, error handling,
// AND offline resilience for the entire trip lifecycle.
//
// Offline strategy:
//   READ hooks (useTrips, useTrip):
//     1. On success → cache response in local offline DB (background, best-effort)
//     2. On transient error (network, 429, 5xx) → serve from offline cache
//     3. Pre-populate query cache with offline data for instant rendering
//
//   WRITE hooks (mutations):
//     1. Try the API call normally
//     2. On transient error → queue mutation in offline sync queue
//     3. SyncEngine replays queued mutations when connectivity returns
//
// Hooks:
//   useTrips()              — Query for paginated trip list
//   useTrip(tripId)         — Query for a single trip with full details
//   useCreateTrip()         — Mutation for creating a new trip
//   useUpdateTrip()         — Mutation for updating a trip
//   useDeleteTrip()         — Mutation for deleting a trip
//   useInviteMember()       — Mutation for inviting a member to a trip
//   useRemoveMember()       — Mutation for removing a member / leaving
//   useUpdateMemberRole()   — Mutation for changing a member's role
//   useCreateTripDay()      — Mutation for creating a trip day
//   useUpdateTripDay()      — Mutation for updating a trip day
//   useDeleteTripDay()      — Mutation for deleting a trip day
//   useCreateTravelPoint()  — Mutation for creating a travel point
//   useUpdateTravelPoint()  — Mutation for updating a travel point
//   useDeleteTravelPoint()  — Mutation for deleting a travel point
//   useCreateChecklistItem()— Mutation for creating a checklist item
//   useUpdateChecklistItem()— Mutation for toggling / editing checklist item
//   useDeleteChecklistItem()— Mutation for deleting a checklist item
//   useReorderChecklistItems() — Mutation for reordering checklist items
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  CreateTravelPointPayload,
  CreateTripDayPayload,
  CreateTripPayload,
  InviteTripMemberPayload,
  ReorderItemsPayload,
  TripListParams,
  UpdateTravelPointPayload,
  UpdateTripDayPayload,
  UpdateTripMemberPayload,
  UpdateTripPayload,
} from '@itravel/shared';

import type {
  ChecklistItemResponse,
  CreateChecklistItemPayload,
  LodgingPayload,
  TravelPointResponse,
  TripDayResponse,
  TripDetailResponse,
  TripListResponse,
  TripMemberResponse,
  TripNotePayload,
  TripResponse,
  UpdateChecklistItemPayload,
} from '@/features/trips/api/trips.api';
import { tripsApi } from '@/features/trips/api/trips.api';
import { withClientId } from '@/lib/sync/client-ids';
import { selectIsOnline, useSyncStore } from '@/store/sync.store';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

/**
 * What the home screen asks for. Shared so the offline layer can seed exactly
 * the key that screen reads — it used to seed the unparameterised list, which
 * nothing renders, so hydration did nothing for the one screen that needed it.
 */
export const HOME_TRIPS_PARAMS: TripListParams = { sort: 'startDate', perPage: 50 };

export const tripKeys = {
  all: ['trips'] as const,
  lists: () => [...tripKeys.all, 'list'] as const,
  /** Home's current or next trip — changes whenever a trip is added, edited or left. */
  current: () => [...tripKeys.all, 'current'] as const,
  list: (params?: TripListParams) => [...tripKeys.lists(), params ?? {}] as const,
  details: () => [...tripKeys.all, 'detail'] as const,
  detail: (tripId: string) => [...tripKeys.details(), tripId] as const,
  checklists: (tripId: string) => [...tripKeys.detail(tripId), 'checklists'] as const,
} as const;

// ===========================================================================
// OFFLINE INFRASTRUCTURE
// ===========================================================================

// ---------------------------------------------------------------------------
// Lazy imports for offline DB (avoids bundling SQLite in every chunk)
// ---------------------------------------------------------------------------

let _offlineDb: Awaited<typeof import('@/lib/db/offline-db')>['offlineDb'] | null = null;

async function getOfflineDb() {
  if (!_offlineDb) {
    try {
      const mod = await import('@/lib/db/offline-db');
      _offlineDb = mod.offlineDb;
    } catch {
      console.warn('[Trips] Could not load offline DB module');
    }
  }
  return _offlineDb;
}

/**
 * The database, once there is actually a session to read from.
 *
 * Screens fetch the moment they mount; the session opens after auth hydration.
 * Online nobody notices, because the request outlasts the race. Offline the
 * request fails in milliseconds, so the fallback to disk used to arrive before
 * there was a database, get told there was no session, and report that the user
 * had no trips — a cold start with no signal showed an error and a Retry button
 * on top of a full cache.
 */
async function getReadableOfflineDb() {
  const db = await getOfflineDb();
  if (!db) return null;
  return (await db.waitForSession()) ? db : null;
}

// ---------------------------------------------------------------------------
// Safe cache operations (never throw — offline caching is best-effort)
// ---------------------------------------------------------------------------

/**
 * Writes a trip to disk without letting a thinner record overwrite a fuller one.
 *
 * The list endpoint returns summaries — no days, no points, no checklist — and
 * they were being written straight over the full detail cached when the trip
 * was opened. Since the home screen refetches its list every two minutes, a
 * trip you had opened and could read offline quietly became a name and a date
 * range with an empty plan. The itinerary was the thing you needed offline.
 *
 * So a payload that carries no `days` is merged on top of what is already
 * stored rather than replacing it: the summary's fields are fresher, the
 * itinerary is the only copy we have.
 */
async function cacheTripDetail(
  tripId: string,
  data: TripDetailResponse | TripResponse,
): Promise<void> {
  try {
    const db = await getOfflineDb();
    if (!db) return;

    const isFullDetail = 'days' in data && Array.isArray((data as TripDetailResponse).days);

    if (isFullDetail) {
      await db.cacheTrip(tripId, data);
      return;
    }

    const existing = (await db.getCachedTrip(tripId)) as TripDetailResponse | null;
    await db.cacheTrip(tripId, existing ? { ...existing, ...data } : data);
  } catch (error) {
    console.warn('[Trips] Failed to cache trip:', error);
  }
}

async function getCachedTrip(tripId: string): Promise<TripDetailResponse | null> {
  try {
    const db = await getReadableOfflineDb();
    if (db) {
      const result = await db.getCachedTrip(tripId);
      return result as TripDetailResponse | null;
    }
  } catch (error) {
    console.warn('[Trips] Failed to read cached trip:', error);
  }
  return null;
}

async function getCachedTripsList(): Promise<TripListResponse | null> {
  try {
    const db = await getReadableOfflineDb();
    if (db) {
      const trips = await db.getAllCachedTrips();
      if (trips && trips.length > 0) {
        return {
          trips: trips.map((t) => {
            const trip = t as TripDetailResponse;
            return {
              id: trip.id,
              name: trip.name,
              description: trip.description,
              coverImageUrl: trip.coverImageUrl,
              startDate: trip.startDate,
              endDate: trip.endDate,
              status: trip.status,
              visibility: trip.visibility,
              defaultCurrency: trip.defaultCurrency ?? 'EUR',
              createdById: trip.createdById,
              createdAt: trip.createdAt,
              updatedAt: trip.updatedAt,
              memberCount: trip.memberCount,
              dayCount: trip.dayCount,
              myRole: trip.myRole,
            };
          }),
          meta: {
            page: 1,
            perPage: trips.length,
            total: trips.length,
            totalPages: 1,
          },
        };
      }
    }
  } catch (error) {
    console.warn('[Trips] Failed to read cached trips list:', error);
  }
  return null;
}

async function removeCachedTrip(tripId: string): Promise<void> {
  try {
    const db = await getOfflineDb();
    if (db) {
      await db.removeCachedTrip(tripId);
    }
  } catch (error) {
    console.warn('[Trips] Failed to remove cached trip:', error);
  }
}

// ---------------------------------------------------------------------------
// Queue a mutation for offline replay
// ---------------------------------------------------------------------------

async function queueOfflineMutation(
  entityType: string,
  entityId: string,
  action: string,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    const store = useSyncStore.getState();
    await store.queueMutation({
      entityType: entityType as import('@itravel/shared').SyncEntityType,
      entityId,
      action: action as import('@itravel/shared').SyncAction,
      payload,
    });
  } catch (error) {
    console.error('[Trips] Failed to queue offline mutation:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Transient error detection
// ---------------------------------------------------------------------------
// A "transient" error is one where the server might be fine but the request
// failed for a recoverable reason: network down, rate limited, server overload.
// For READ operations we fall back to the offline cache.
// For WRITE operations we queue the mutation for later replay.
// ---------------------------------------------------------------------------

function isTransientError(error: unknown): boolean {
  if (!error) return false;

  // 1. True network errors (no response at all)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // 2. ky HTTPError — check the status code
  //    ky's HTTPError has a `response` property with the HTTP response
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response: Response }).response;
    if (response && typeof response.status === 'number') {
      const status = response.status;
      // 429 Too Many Requests (rate limited)
      // 408 Request Timeout
      // 500, 502, 503, 504 — server errors
      if (status === 429 || status === 408 || status >= 500) {
        return true;
      }
    }
    // Has a response but not a transient status code → client error (4xx), don't fallback
    return false;
  }

  // 3. String-based pattern matching for edge cases
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch') ||
    message.includes('Network request failed') ||
    message.includes('NetworkError') ||
    message.includes('net::ERR_') ||
    message.includes('Load failed') ||
    message.includes('The Internet connection appears to be offline') ||
    message.includes('RATE_LIMITED') ||
    message.includes('Too Many Requests') ||
    message.includes('timeout') ||
    message.includes('ETIMEDOUT') ||
    message.includes('ECONNREFUSED')
  );
}

// ===========================================================================
// TRIP QUERIES (offline-aware)
// ===========================================================================

// ---------------------------------------------------------------------------
// useTrips — Paginated trip list (with offline fallback)
// ---------------------------------------------------------------------------

interface UseTripsOptions {
  params?: TripListParams;
  enabled?: boolean;
}

/**
 * Query hook for fetching a paginated list of trips the user is a member of.
 *
 * **Offline behavior:**
 * - On success: caches each trip in the offline DB (background)
 * - On transient error (429, network, 5xx): serves from offline cache
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useTrips({ params: { status: 'PLANNING' } });
 * ```
 */
export function useTrips(options?: UseTripsOptions) {
  const { params, enabled = true } = options ?? {};
  const isOnline = useSyncStore(selectIsOnline);

  return useQuery<TripListResponse>({
    queryKey: tripKeys.list(params),
    queryFn: async () => {
      try {
        const result = await tripsApi.getTrips(params);

        // Background: cache each trip for offline access
        Promise.resolve().then(async () => {
          for (const trip of result.trips) {
            try {
              await cacheTripDetail(trip.id, trip);
            } catch {
              // Best-effort caching — swallow errors
            }
          }
        });

        return result;
      } catch (error) {
        // On transient errors, serve from cache if available
        if (isTransientError(error)) {
          console.info('[Trips] Transient error fetching trip list — trying offline cache', error);
          const cached = await getCachedTripsList();
          if (cached) {
            return cached;
          }
        }
        throw error;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: isOnline ? 1 : 0, // Fewer retries — ky already retries on 429/5xx
  });
}

// ---------------------------------------------------------------------------
// useTrip — Single trip with full details (with offline fallback)
// ---------------------------------------------------------------------------

interface UseTripOptions {
  enabled?: boolean;
}

/**
 * Query hook for fetching a single trip with members, days, points, and checklist.
 *
 * **Offline behavior:**
 * - Pre-populates the query cache with offline-cached data for instant rendering
 * - On success: caches full trip detail in offline DB
 * - On transient error (429, network, 5xx): serves from offline cache
 *
 * Usage:
 * ```tsx
 * const { data: trip, isLoading } = useTrip(tripId);
 * ```
 */
export function useTrip(tripId: string | undefined, options?: UseTripOptions) {
  const { enabled = true } = options ?? {};
  const isOnline = useSyncStore(selectIsOnline);
  const queryClient = useQueryClient();

  // Pre-populate with cached data so the UI renders instantly
  const hasPrimed = useRef(false);
  useEffect(() => {
    if (!tripId || hasPrimed.current) return;
    hasPrimed.current = true;

    getCachedTrip(tripId).then((cached) => {
      if (cached) {
        const existing = queryClient.getQueryData(tripKeys.detail(tripId));
        if (!existing) {
          queryClient.setQueryData(tripKeys.detail(tripId), cached);
        }
      }
    });
  }, [tripId, queryClient]);

  return useQuery<TripDetailResponse>({
    queryKey: tripKeys.detail(tripId ?? ''),
    queryFn: async () => {
      try {
        const result = await tripsApi.getTripById(tripId!);

        // Cache the full trip detail in the background
        cacheTripDetail(tripId!, result).catch(() => {
          // Best-effort caching
        });

        return result;
      } catch (error) {
        // On transient errors, serve from cache if available
        if (isTransientError(error)) {
          console.info(
            `[Trips] Transient error fetching trip ${tripId} — trying offline cache`,
            error,
          );
          const cached = await getCachedTrip(tripId!);
          if (cached) {
            return cached;
          }
        }
        throw error;
      }
    },
    enabled: enabled && !!tripId,
    staleTime: 1000 * 60 * 1, // 1 minute
    retry: isOnline ? 1 : 0,
  });
}

// ===========================================================================
// TRIP MUTATIONS (offline-aware)
// ===========================================================================

// ---------------------------------------------------------------------------
// useCreateTrip
// ---------------------------------------------------------------------------

interface UseCreateTripOptions {
  onSuccess?: (trip: TripDetailResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for creating a new trip.
 *
 * On success:
 * - Invalidates the trip list cache
 * - Caches the new trip in offline DB
 * - Navigates to the new trip's detail page (unless overridden via onSuccess)
 *
 * On transient error:
 * - Queues the mutation for offline replay
 */
export function useCreateTrip(options?: UseCreateTripOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<TripDetailResponse, Error, CreateTripPayload>({
    mutationFn: async (rawPayload) => {
      const payload = withClientId(rawPayload);
      try {
        return await tripsApi.createTrip(payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info('[Trips] Queueing offline CREATE trip');
          await queueOfflineMutation(
            'trip',
            payload.id,
            'CREATE',
            payload as unknown as Record<string, unknown>,
          );
          // Return a placeholder so the UI can proceed
          return undefined as unknown as TripDetailResponse;
        }
        throw error;
      }
    },
    onSuccess: (trip) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tripKeys.current() });

      if (trip?.id) {
        queryClient.setQueryData(tripKeys.detail(trip.id), trip);
        cacheTripDetail(trip.id, trip);
      }

      if (options?.onSuccess) {
        options.onSuccess(trip);
      } else if (trip?.id) {
        navigate(`/trips/${trip.id}`, { replace: true });
      } else {
        // Queued offline — navigate to list
        navigate('/trips', { replace: true });
      }
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateTrip
// ---------------------------------------------------------------------------

interface UseUpdateTripOptions {
  onSuccess?: (trip: TripDetailResponse) => void;
  onError?: (error: Error) => void;
}

interface UpdateTripVariables {
  tripId: string;
  payload: UpdateTripPayload;
}

/**
 * Mutation hook for updating a trip.
 */
export function useUpdateTrip(options?: UseUpdateTripOptions) {
  const queryClient = useQueryClient();

  return useMutation<TripDetailResponse, Error, UpdateTripVariables>({
    mutationFn: async ({ tripId, payload }) => {
      try {
        return await tripsApi.updateTrip(tripId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline UPDATE trip ${tripId}`);
          await queueOfflineMutation('trip', tripId, 'UPDATE', {
            tripId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TripDetailResponse;
        }
        throw error;
      }
    },
    onSuccess: (trip) => {
      if (trip?.id) {
        queryClient.setQueryData(tripKeys.detail(trip.id), trip);
        cacheTripDetail(trip.id, trip);
      }
      queryClient.invalidateQueries({ queryKey: tripKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tripKeys.current() });
      options?.onSuccess?.(trip);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteTrip
// ---------------------------------------------------------------------------

interface UseDeleteTripOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for deleting a trip.
 */
export function useDeleteTrip(options?: UseDeleteTripOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<string, Error, string>({
    mutationFn: async (tripId) => {
      try {
        return await tripsApi.deleteTrip(tripId);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline DELETE trip ${tripId}`);
          await queueOfflineMutation('trip', tripId, 'DELETE', { tripId });
          return 'queued-offline';
        }
        throw error;
      }
    },
    onSuccess: (_message, tripId) => {
      queryClient.removeQueries({ queryKey: tripKeys.detail(tripId) });
      queryClient.invalidateQueries({ queryKey: tripKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tripKeys.current() });
      removeCachedTrip(tripId);

      if (options?.onSuccess) {
        options.onSuccess();
      } else {
        navigate('/trips', { replace: true });
      }
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ===========================================================================
// MEMBER MUTATIONS (offline-aware)
// ===========================================================================

// ---------------------------------------------------------------------------
// useInviteMember
// ---------------------------------------------------------------------------

interface InviteMemberVariables {
  tripId: string;
  payload: InviteTripMemberPayload;
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation<TripMemberResponse, Error, InviteMemberVariables>({
    mutationFn: async ({ tripId, payload }) => {
      try {
        return await tripsApi.inviteMember(tripId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline INVITE member for trip ${tripId}`);
          await queueOfflineMutation('trip_member', tripId, 'CREATE', {
            tripId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TripMemberResponse;
        }
        throw error;
      }
    },
    onSuccess: (_member, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
      queryClient.invalidateQueries({ queryKey: tripKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tripKeys.current() });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateMemberRole
// ---------------------------------------------------------------------------

interface UpdateMemberRoleVariables {
  tripId: string;
  userId: string;
  payload: UpdateTripMemberPayload;
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation<TripMemberResponse, Error, UpdateMemberRoleVariables>({
    mutationFn: async ({ tripId, userId, payload }) => {
      try {
        return await tripsApi.updateMemberRole(tripId, userId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline UPDATE member role for trip ${tripId}`);
          await queueOfflineMutation('trip_member', tripId, 'UPDATE', {
            tripId,
            userId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TripMemberResponse;
        }
        throw error;
      }
    },
    onSuccess: (_member, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useRemoveMember
// ---------------------------------------------------------------------------

interface RemoveMemberVariables {
  tripId: string;
  userId: string;
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, RemoveMemberVariables>({
    mutationFn: async ({ tripId, userId }) => {
      try {
        return await tripsApi.removeMember(tripId, userId);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline REMOVE member from trip ${tripId}`);
          await queueOfflineMutation('trip_member', tripId, 'DELETE', {
            tripId,
            userId,
          });
          return 'queued-offline';
        }
        throw error;
      }
    },
    onSuccess: (_message, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
      queryClient.invalidateQueries({ queryKey: tripKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tripKeys.current() });
    },
  });
}

// ===========================================================================
// TRIP DAY MUTATIONS (offline-aware)
// ===========================================================================

// ---------------------------------------------------------------------------
// useCreateTripDay
// ---------------------------------------------------------------------------

interface CreateTripDayVariables {
  tripId: string;
  payload: CreateTripDayPayload;
}

export function useCreateTripDay() {
  const queryClient = useQueryClient();

  return useMutation<TripDayResponse, Error, CreateTripDayVariables>({
    mutationFn: async ({ tripId, payload }) => {
      try {
        return await tripsApi.createTripDay(tripId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline CREATE day for trip ${tripId}`);
          await queueOfflineMutation('trip_day', tripId, 'CREATE', {
            tripId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TripDayResponse;
        }
        throw error;
      }
    },
    onSuccess: (_day, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateTripDay
// ---------------------------------------------------------------------------

interface UpdateTripDayVariables {
  tripId: string;
  dayId: string;
  payload: UpdateTripDayPayload;
}

export function useUpdateTripDay() {
  const queryClient = useQueryClient();

  return useMutation<TripDayResponse, Error, UpdateTripDayVariables>({
    mutationFn: async ({ tripId, dayId, payload }) => {
      try {
        return await tripsApi.updateTripDay(tripId, dayId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline UPDATE day ${dayId} for trip ${tripId}`);
          await queueOfflineMutation('trip_day', dayId, 'UPDATE', {
            tripId,
            dayId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TripDayResponse;
        }
        throw error;
      }
    },
    onSuccess: (_day, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteTripDay
// ---------------------------------------------------------------------------

interface DeleteTripDayVariables {
  tripId: string;
  dayId: string;
}

export function useDeleteTripDay() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, DeleteTripDayVariables>({
    mutationFn: async ({ tripId, dayId }) => {
      try {
        return await tripsApi.deleteTripDay(tripId, dayId);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline DELETE day ${dayId} for trip ${tripId}`);
          await queueOfflineMutation('trip_day', dayId, 'DELETE', {
            tripId,
            dayId,
          });
          return 'queued-offline';
        }
        throw error;
      }
    },
    onSuccess: (_message, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ===========================================================================
// TRAVEL POINT MUTATIONS (offline-aware)
// ===========================================================================

// ---------------------------------------------------------------------------
// useCreateTravelPoint
// ---------------------------------------------------------------------------

interface CreateTravelPointVariables {
  tripId: string;
  /** Null adds it unscheduled. */
  dayId: string | null;
  payload: CreateTravelPointPayload;
}

export function useCreateTravelPoint() {
  const queryClient = useQueryClient();

  return useMutation<TravelPointResponse, Error, CreateTravelPointVariables>({
    mutationFn: async ({ tripId, dayId, payload: rawPayload }) => {
      const payload = withClientId(rawPayload);
      try {
        return await tripsApi.createTravelPoint(tripId, dayId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline CREATE point for day ${dayId}`);
          await queueOfflineMutation('travel_point', payload.id, 'CREATE', {
            tripId,
            dayId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TravelPointResponse;
        }
        throw error;
      }
    },
    onSuccess: (_point, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateTravelPoint
// ---------------------------------------------------------------------------

interface UpdateTravelPointVariables {
  tripId: string;
  dayId: string;
  pointId: string;
  payload: UpdateTravelPointPayload;
}

export function useUpdateTravelPoint() {
  const queryClient = useQueryClient();

  return useMutation<TravelPointResponse, Error, UpdateTravelPointVariables>({
    mutationFn: async ({ tripId, dayId, pointId, payload }) => {
      try {
        return await tripsApi.updateTravelPoint(tripId, dayId, pointId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline UPDATE point ${pointId}`);
          await queueOfflineMutation('travel_point', pointId, 'UPDATE', {
            tripId,
            dayId,
            pointId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as TravelPointResponse;
        }
        throw error;
      }
    },
    onSuccess: (_point, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteTravelPoint
// ---------------------------------------------------------------------------

interface DeleteTravelPointVariables {
  tripId: string;
  dayId: string;
  pointId: string;
}

export function useDeleteTravelPoint() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, DeleteTravelPointVariables>({
    mutationFn: async ({ tripId, dayId, pointId }) => {
      try {
        return await tripsApi.deleteTravelPoint(tripId, dayId, pointId);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline DELETE point ${pointId}`);
          await queueOfflineMutation('travel_point', pointId, 'DELETE', {
            tripId,
            dayId,
            pointId,
          });
          return 'queued-offline';
        }
        throw error;
      }
    },
    onSuccess: (_message, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeletePointFromTrip
// ---------------------------------------------------------------------------

export function useDeletePointFromTrip() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, { tripId: string; pointId: string }>({
    mutationFn: ({ tripId, pointId }) => tripsApi.deletePointFromTrip(tripId, pointId),
    onSuccess: (_msg, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useUnschedulePoint
// ---------------------------------------------------------------------------

export function useUnschedulePoint() {
  const queryClient = useQueryClient();

  return useMutation<TravelPointResponse, Error, { tripId: string; pointId: string }>({
    mutationFn: ({ tripId, pointId }) => tripsApi.unschedulePoint(tripId, pointId),
    onSuccess: (_point, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useAssignPointToDay
// ---------------------------------------------------------------------------

export function useAssignPointToDay() {
  const queryClient = useQueryClient();

  return useMutation<
    TravelPointResponse,
    Error,
    { tripId: string; pointId: string; dayId: string }
  >({
    mutationFn: ({ tripId, pointId, dayId }) => tripsApi.assignPointToDay(tripId, pointId, dayId),
    onSuccess: (_point, { tripId }) => {
      // Invalidate trip detail so unscheduledPoints and day points refresh
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ===========================================================================
// CHECKLIST MUTATIONS (offline-aware)
// ===========================================================================

// ---------------------------------------------------------------------------
// useCreateChecklistItem
// ---------------------------------------------------------------------------

interface CreateChecklistItemVariables {
  tripId: string;
  payload: CreateChecklistItemPayload;
}

// ---------------------------------------------------------------------------
// Checklist lists
// ---------------------------------------------------------------------------

export function useChecklistLists(tripId: string | undefined) {
  return useQuery({
    queryKey: tripKeys.checklists(tripId ?? ''),
    queryFn: () => tripsApi.getChecklistLists(tripId!),
    enabled: Boolean(tripId),
  });
}

export function useCreateChecklistList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tripId, name }: { tripId: string; name: string }) =>
      tripsApi.createChecklistList(tripId, { name }),
    onSuccess: (_list, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.checklists(tripId) });
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

export function useRenameChecklistList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tripId, listId, name }: { tripId: string; listId: string; name: string }) =>
      tripsApi.updateChecklistList(tripId, listId, { name }),
    onSuccess: (_list, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.checklists(tripId) });
    },
  });
}

export function useSetChecklistListMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tripId,
      listId,
      userIds,
    }: {
      tripId: string;
      listId: string;
      userIds: string[] | null;
    }) => tripsApi.setChecklistListMembers(tripId, listId, userIds),
    onSuccess: (_list, { tripId }) => {
      // The list may have just moved out of somebody's sight, so the whole
      // collection is re-read rather than the one row patched.
      queryClient.invalidateQueries({ queryKey: tripKeys.checklists(tripId) });
    },
  });
}

export function useDeleteChecklistList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tripId, listId }: { tripId: string; listId: string }) =>
      tripsApi.deleteChecklistList(tripId, listId),
    onSuccess: (_msg, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.checklists(tripId) });
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

export function useCreateChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation<ChecklistItemResponse, Error, CreateChecklistItemVariables>({
    mutationFn: async ({ tripId, payload: rawPayload }) => {
      const payload = withClientId(rawPayload);
      try {
        return await tripsApi.createChecklistItem(tripId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline CREATE checklist item for trip ${tripId}`);
          await queueOfflineMutation('checklist_item', payload.id, 'CREATE', {
            tripId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as ChecklistItemResponse;
        }
        throw error;
      }
    },
    onSuccess: (_item, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
      queryClient.invalidateQueries({ queryKey: tripKeys.checklists(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateChecklistItem
// ---------------------------------------------------------------------------

interface UpdateChecklistItemVariables {
  tripId: string;
  itemId: string;
  payload: UpdateChecklistItemPayload;
}

/**
 * Mutation hook for updating a checklist item.
 * Supports optimistic updates for the `isChecked` toggle to feel instant.
 */
export function useUpdateChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation<
    ChecklistItemResponse,
    Error,
    UpdateChecklistItemVariables,
    { previousTrip: TripDetailResponse | undefined }
  >({
    mutationFn: async ({ tripId, itemId, payload }) => {
      try {
        return await tripsApi.updateChecklistItem(tripId, itemId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline UPDATE checklist item ${itemId}`);
          await queueOfflineMutation('checklist_item', itemId, 'UPDATE', {
            tripId,
            itemId,
            ...payload,
          } as unknown as Record<string, unknown>);
          return undefined as unknown as ChecklistItemResponse;
        }
        throw error;
      }
    },

    // Optimistic update for checkbox toggle
    onMutate: async ({ tripId, itemId, payload }) => {
      await queryClient.cancelQueries({ queryKey: tripKeys.detail(tripId) });

      const previousTrip = queryClient.getQueryData<TripDetailResponse>(tripKeys.detail(tripId));

      if (previousTrip && payload.isChecked !== undefined) {
        const updatedTrip: TripDetailResponse = {
          ...previousTrip,
          checklist: previousTrip.checklist.map((item) =>
            item.id === itemId ? { ...item, isChecked: payload.isChecked! } : item,
          ),
        };
        queryClient.setQueryData(tripKeys.detail(tripId), updatedTrip);
      }

      return { previousTrip };
    },

    onError: (_error, { tripId }, context) => {
      if (context?.previousTrip) {
        queryClient.setQueryData(tripKeys.detail(tripId), context.previousTrip);
      }
    },

    onSettled: (_data, _error, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteChecklistItem
// ---------------------------------------------------------------------------

interface DeleteChecklistItemVariables {
  tripId: string;
  itemId: string;
}

export function useDeleteChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, DeleteChecklistItemVariables>({
    mutationFn: async ({ tripId, itemId }) => {
      try {
        return await tripsApi.deleteChecklistItem(tripId, itemId);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline DELETE checklist item ${itemId}`);
          await queueOfflineMutation('checklist_item', itemId, 'DELETE', {
            tripId,
            itemId,
          });
          return 'queued-offline';
        }
        throw error;
      }
    },
    onSuccess: (_message, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useReorderChecklistItems
// ---------------------------------------------------------------------------

interface ReorderChecklistItemsVariables {
  tripId: string;
  payload: ReorderItemsPayload;
}

export function useReorderChecklistItems() {
  const queryClient = useQueryClient();

  return useMutation<ChecklistItemResponse[], Error, ReorderChecklistItemsVariables>({
    mutationFn: async ({ tripId, payload }) => {
      try {
        return await tripsApi.reorderChecklistItems(tripId, payload);
      } catch (error) {
        if (isTransientError(error)) {
          console.info(`[Trips] Queueing offline REORDER checklist for trip ${tripId}`);
          await queueOfflineMutation('checklist_item', tripId, 'REORDER', {
            tripId,
            orderedIds: payload.orderedIds,
          });
          return [] as ChecklistItemResponse[];
        }
        throw error;
      }
    },
    onSuccess: (_items, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Lodgings and important info — online only; both come with the trip detail
// ---------------------------------------------------------------------------

export function useSaveLodging() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tripId,
      lodgingId,
      payload,
    }: {
      tripId: string;
      /** Absent to add one. */
      lodgingId?: string;
      payload: LodgingPayload;
    }) =>
      lodgingId
        ? tripsApi.updateLodging(tripId, lodgingId, payload)
        : tripsApi.createLodging(tripId, payload),
    onSuccess: (_lodging, { tripId }) =>
      void queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) }),
  });
}

export function useDeleteLodging() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tripId, lodgingId }: { tripId: string; lodgingId: string }) =>
      tripsApi.deleteLodging(tripId, lodgingId),
    onSuccess: (_message, { tripId }) =>
      void queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) }),
  });
}

export function useSaveTripNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tripId,
      noteId,
      payload,
    }: {
      tripId: string;
      /** Absent to add one. */
      noteId?: string;
      payload: Partial<TripNotePayload>;
    }) =>
      noteId
        ? tripsApi.updateTripNote(tripId, noteId, payload)
        : tripsApi.createTripNote(tripId, payload as TripNotePayload),
    onSuccess: (_note, { tripId }) =>
      void queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) }),
  });
}

export function useDeleteTripNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tripId, noteId }: { tripId: string; noteId: string }) =>
      tripsApi.deleteTripNote(tripId, noteId),
    onSuccess: (_message, { tripId }) =>
      void queryClient.invalidateQueries({ queryKey: tripKeys.detail(tripId) }),
  });
}
